/** The API client. ADR-0027: `openapi-fetch` over types generated from the OpenAPI document.
 *
 * The types in `schema.d.ts` are generated and committed, so a change to the API shows up as a
 * diff in code review — the contract test until ADR-0016's snapshot gate exists. Regenerate with
 * `make api-types`.
 *
 * The reason this is worth a dependency: the generated types declare every amount as `string`.
 * Treating an emission figure as a number is therefore a **compile error**, not a habit someone
 * has to remember in review. Given that the predecessor project shipped a test vector wrong by
 * 20 kg, moving numeric correctness into the type system is cheap at the price.
 */

import createClient from "openapi-fetch";

import { scrubInternal } from "../lib/customerWords";
import { joinApiUrl, resolveApiOrigin } from "./origin";
import type { paths } from "./schema";

/** The API's origin, from the build — **ADR-0049, option D1**.
 *
 * Production is `https://app.<domain>` talking to `https://api.<domain>`: cross-origin, same-site,
 * so the `SameSite=Lax` session cookie travels *if* the request asks for it (`credentials` below)
 * and *if* the page's CSP lets it connect (`connect-src` in `index.html`, which the build checks
 * against this value — see `originGuard.ts`).
 *
 * In development the variable is unset and Vite proxies `/api` to the backend, so requests are
 * relative and same-origin. **An unset variable in a production build throws** rather than falling
 * back to that relative path — `origin.ts` has the argument. `VITE_API_ORIGIN=` set to the empty
 * string is the explicit way to say "same-origin" (CI's `vite preview` behind its proxy).
 */
export const API_ORIGIN = resolveApiOrigin(import.meta.env.VITE_API_ORIGIN, import.meta.env.PROD);

/** A root-relative API path (`/api/v1/…`) as the URL to request or link to.
 *
 * **Every address that reaches the API goes through this** — `fetch` calls, and also the `href` of
 * a download link, which is a navigation the browser resolves against the *page's* origin. A bare
 * `/api/v1/…` there is `app.<domain>/api/v1/…`: CloudFront serving the SPA's own HTML.
 */
export function apiUrl(path: string): string {
  return joinApiUrl(API_ORIGIN, path);
}

/** `fetch` for the calls `openapi-fetch` cannot make (multipart bodies, a file in the response).
 *
 * It exists so that the two settings a cross-origin API needs are not each remembered at five call
 * sites: the base URL, and **`credentials: "include"`**, without which the browser sends no cookie
 * cross-origin and every call arrives signed out. `credentials` is applied *after* the caller's
 * `init`, so a call site cannot quietly opt out of it.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(apiUrl(path), { ...init, credentials: "include" });
}

/** `credentials: "include"` on the client itself, so every `api.GET`/`api.POST` carries the session
 * cookie cross-origin without a call site having to ask. Same-origin it changes nothing. */
export const api = createClient<paths>({ baseUrl: API_ORIGIN, credentials: "include" });

/** The fields whose value is **our own sentence about a rule**, not something a customer typed.
 *
 * A regime rule's citation, limitation, dispute note and in-force caveat are written in
 * `regimes/definitions.py` and `support.py`, where they cite the decision register (`(U-03, U-15)`,
 * `C-05 is DISPUTED`, `ADR-0032 option 2`). Changing that text is a `RegimeSpec.version` bump (the
 * loader compares it), so the words are said for a customer **here, once, where the response enters
 * the SPA**, and not at the fifty places that print one (U8, `lib/customerWords.ts`).
 *
 * **By field name, never by value.** A customer's own text can look like a register id (`PO-12`, a
 * vendor's invoice number), so nothing outside this list is touched, and a `statement` or
 * `description` is only touched when its object also carries a `citation`, which is what makes it a
 * rule's sentence.
 */
const RULE_PROSE_FIELDS: ReadonlySet<string> = new Set([
  "citation",
  "limitation",
  "dispute",
  "dispute_note",
  "in_force_caveat",
  "due_date_basis",
  "support_because",
  "verdict_reason",
]);

/** Cheap pre-test: parsing every response twice would be a cost nobody agreed to. */
const MAY_CARRY_A_CODE = /ADR-\d|\b[A-Z]{1,4}-\d{2}[a-z]?\b|DISPUTED/;

function scrubTree(node: unknown): { value: unknown; changed: boolean } {
  if (Array.isArray(node)) {
    let changed = false;
    const value = node.map((item) => {
      const out = scrubTree(item);
      changed ||= out.changed;
      return out.value;
    });
    return { value: changed ? value : node, changed };
  }
  if (node !== null && typeof node === "object") {
    const record = node as Record<string, unknown>;
    const isRule = typeof record.citation === "string";
    let changed = false;
    const value: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(record)) {
      if (typeof item === "string" && (RULE_PROSE_FIELDS.has(key) || (isRule && (key === "statement" || key === "description")))) {
        const scrubbed = scrubInternal(item);
        changed ||= scrubbed !== item;
        value[key] = scrubbed;
      } else {
        const out = scrubTree(item);
        changed ||= out.changed;
        value[key] = out.value;
      }
    }
    return { value: changed ? value : node, changed };
  }
  return { value: node, changed: false };
}

/** The response with a rule's sentences said without the register ids, or `undefined` (leave it) when
 * there was nothing to say differently. Only JSON is read, and a body that does not parse is left alone. */
export async function scrubRuleProse(response: Response): Promise<Response | undefined> {
  if (!response.headers.get("content-type")?.includes("json")) return undefined;
  const text = await response.clone().text();
  if (!MAY_CARRY_A_CODE.test(text)) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  const { value, changed } = scrubTree(parsed);
  if (!changed) return undefined;
  const headers = new Headers(response.headers);
  // The body is a different length now, and was already decoded by `text()`.
  headers.delete("content-length");
  headers.delete("content-encoding");
  return new Response(JSON.stringify(value), {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

/** What to do when the server says this session is over — **EXP-29's "expired access"**.
 *
 * A session expires on the server's clock, and a focused tab has no way to know. `useMe` re-checks
 * on focus, which covers a tab left open overnight; it does **not** cover a session that ends while
 * somebody is working. Then the next data request 401s, and before this the screen said *"could not
 * be loaded — try again"*.
 *
 * That sentence is worse than useless: it is **the wrong remedy**, and retrying fails identically
 * for ever with nothing explaining why. The right remedy is the sign-in form, and there is already
 * a component that renders it (`RequireSession`) off a single fact (`useMe`). So a 401 anywhere
 * resets that one fact, in one place, and every screen follows.
 *
 * A **registered handler** rather than importing the query client here: the client is created in
 * `main.tsx`, a module-level singleton would be the wrong client in a test, and a handler can be
 * registered by a test and asserted to fire.
 */
let sessionLost: (() => void) | null = null;

export function onSessionLost(handler: () => void): void {
  sessionLost = handler;
}

api.use({
  async onResponse({ request, response }) {
    // **`/auth/` is excluded, and both reasons matter.** `GET /auth/me` answers 401 to mean "not
    // signed in", which is the question rather than a failure; and a failed sign-in attempt must
    // not be reported to the reader as an expired session — they never had one.
    if (response.status === 401 && !new URL(request.url).pathname.startsWith("/api/v1/auth/")) {
      sessionLost?.();
    }
    return scrubRuleProse(response);
  },
});

type Schemas = paths extends { [k: string]: never } ? never : NonNullable<unknown>;
export type { Schemas };

/** Query keys, in one place.
 *
 * Cache invalidation is the cost ADR-0027 accepted, and the failure mode it named is a stale figure
 * sitting next to a fresh one — the worst class of bug this product can have. Keys are therefore
 * built here rather than inline, so invalidating "everything for this period" is one call and
 * cannot miss a query someone added later.
 *
 * **No organisation in any key.** There was one, because the demo endpoints took an `org_id`; the
 * authenticated ones do not, and a session names one organisation. Keying by an organisation the
 * client cannot choose would only invite a cache that outlives the session it belongs to — so
 * switching workspaces clears the cache instead.
 */
export const keys = {
  organization: () => ["organization"] as const,
  memberships: () => ["memberships"] as const,
  // `evidence` asks for the traceability summary too, which is a different (dearer) read.
  inventory: (from: string, to: string, method: string, evidence = false) =>
    evidence
      ? (["inventory", from, to, method, "evidence"] as const)
      : (["inventory", from, to, method] as const),
  // Under `["inventory"]` on purpose: whatever invalidates the totals (a recalculation, an import,
  // a correction) invalidates the series drawn from the same rows, so the two cannot be refreshed
  // apart. The literal "monthly" can never collide with a date, which is what sits second in
  // `inventory` above.
  inventoryMonthly: (from: string, to: string, method: string, facilityId?: string) =>
    facilityId
      ? (["inventory", "monthly", from, to, method, facilityId] as const)
      : (["inventory", "monthly", from, to, method] as const),
  // A ranked excerpt (the largest `rows`, one page) - a different read from the whole window, so
  // its own key, under `["figures"]` so a recalculation invalidates both together.
  figuresTop: (from: string, to: string, method: string, scope: string | undefined, rows: number) =>
    ["figures", "top", from, to, method, scope ?? "all", rows] as const,
  // A **cut** of the window's figures (a category, a site, a grade...), read a page at a time for a
  // drawer. Its own key under `["figures"]`: a different read from the whole window, and a
  // recalculation invalidates it with the rest.
  figuresCut: (from: string, to: string, method: string, cut: string) =>
    ["figures", "cut", from, to, method, cut] as const,
  // What a figure list can be narrowed by (sites, categories, carriers, months, instruments) in a
  // window and scope. Under `["figures"]`: a new figure can add a value, so a recalculation clears it.
  figureFacets: (from: string, to: string, method: string, scope: string | undefined) =>
    ["figures", "facets", from, to, method, scope ?? "all"] as const,
  // Per-site data-quality counts for one scope, over every figure of it. Under `["figures"]` too.
  qualityBySite: (from: string, to: string, method: string, scope: string) =>
    ["figures", "quality-by-site", from, to, method, scope] as const,
  // A receipt is the same figure with or without the filing it was opened from, but not the same
  // response: the filing adds the rules version it was issued under (UX-02), so it is part of the key.
  receipt: (calculationId: string, filingArtifactId?: string) =>
    ["receipt", calculationId, filingArtifactId ?? null] as const,
  obligations: () => ["obligations"] as const,
  // Keyed by the **obligation**, not by a regime and a window. ADR-0029: a report is about one
  // filing, and `(regime, dates)` could not say which of an organisation's filings it meant.
  report: (obligationId: string, method: string) => ["report", obligationId, method] as const,
};
